import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';

export interface VolumeDiscount {
  minQuantity: number;
  discountPercent: number;
  label: string;
}

@Component({
  selector: 'app-volume-discount',
  standalone: false,
  templateUrl: './volume-discount.component.html',
  styleUrl: './volume-discount.component.scss'
})
export class VolumeDiscountComponent implements OnInit {
  @Input() volumeDiscounts: VolumeDiscount[] = [];
  @Input() currentQuantity: number = 1;
  @Input() basePrice: number = 0;
  @Output() quantityChange = new EventEmitter<number>();
  @Output() discountSelect = new EventEmitter<VolumeDiscount>();

  ngOnInit(): void {
    // Sort discounts by minQuantity for proper display
    this.volumeDiscounts = this.volumeDiscounts.sort((a, b) => a.minQuantity - b.minQuantity);
  }

  getActiveDiscount(): VolumeDiscount | null {
    if (!this.volumeDiscounts || this.volumeDiscounts.length === 0) {
      return null;
    }

    // Find the highest applicable discount for current quantity
    let activeDiscount: VolumeDiscount | null = null;
    for (const discount of this.volumeDiscounts) {
      if (this.currentQuantity >= discount.minQuantity) {
        activeDiscount = discount;
      }
    }
    return activeDiscount;
  }

  getDiscountedPrice(discount: VolumeDiscount): number {
    return this.basePrice * (1 - discount.discountPercent / 100);
  }

  calculateSavings(discount: VolumeDiscount): number {
    return this.basePrice - this.getDiscountedPrice(discount);
  }

  // New methods for correct total calculations
  getTotalPrice(discount: VolumeDiscount): number {
    const quantity = this.isActiveDiscount(discount) ? this.currentQuantity : discount.minQuantity;
    return this.basePrice * quantity;
  }

  getDiscountedTotalPrice(discount: VolumeDiscount): number {
    const quantity = this.isActiveDiscount(discount) ? this.currentQuantity : discount.minQuantity;
    const totalPrice = this.basePrice * quantity;
    return totalPrice * (1 - discount.discountPercent / 100);
  }

  getTotalSavings(discount: VolumeDiscount): number {
    const quantity = this.isActiveDiscount(discount) ? this.currentQuantity : discount.minQuantity;
    const totalPrice = this.basePrice * quantity;
    return totalPrice * (discount.discountPercent / 100);
  }

  isActiveDiscount(discount: VolumeDiscount): boolean {
    const active = this.getActiveDiscount();
    return active?.minQuantity === discount.minQuantity;
  }

  isAvailableDiscount(discount: VolumeDiscount): boolean {
    return this.currentQuantity >= discount.minQuantity;
  }

  getNextTierDiscount(): VolumeDiscount | null {
    const currentDiscount = this.getActiveDiscount();
    const nextTiers = this.volumeDiscounts.filter(d => d.minQuantity > this.currentQuantity);
    return nextTiers.length > 0 ? nextTiers[0] : null;
  }

  selectVolumeDiscount(discount: VolumeDiscount): void {
    // Only emit the discount selection - the parent will handle quantity change
    this.discountSelect.emit(discount);
  }

  canSelectDiscount(discount: VolumeDiscount): boolean {
    // Allow selection of any tier that's not currently active
    return !this.isActiveDiscount(discount);
  }

  isClickableDiscount(discount: VolumeDiscount): boolean {
    // All discounts are clickable except the currently active one
    return !this.isActiveDiscount(discount);
  }
}
